import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, Snowflake, ThumbsUp, Search } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { menuData } from "../lib/menuData";
import { Input } from "../components/ui/input";
import { MenuItemCard } from "../components/MenuItemCard";

export default function Menu() {
  const { t } = useLang();
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");

  const categories = [
    { id: "all", label: t.menuPage.categories.all },
    { id: "hayalan", label: t.menuPage.categories.hayalan },
    { id: "arabica", label: t.menuPage.categories.arabica },
    { id: "noncoffee", label: t.menuPage.categories.noncoffee },
    { id: "tea", label: t.menuPage.categories.tea },
    { id: "mocktail", label: t.menuPage.categories.mocktail },
    { id: "signature", label: t.menuPage.categories.signature },
  ];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const groups =
      activeCat === "all"
        ? Object.keys(menuData).map((key) => ({ key, items: menuData[key] }))
        : [{ key: activeCat, items: menuData[activeCat] || [] }];

    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((it) =>
          q ? it.name.toLowerCase().includes(q) : true
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [activeCat, query]);

  return (
    <main data-testid="menu-page" className="bg-brand-cream min-h-screen pb-32">
      {/* Header */}
      <section className="relative pt-28 md:pt-36 pb-10 md:pb-14 bg-brand-cream">
        <div className="absolute top-32 -left-20 w-[360px] h-[360px] rounded-full bg-brand-blue/8 blur-[120px] pointer-events-none" />
        <div className="absolute top-20 right-0 w-[300px] h-[300px] rounded-full bg-brand-gold/15 blur-[100px] pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <Link
            to="/"
            data-testid="menu-back-link"
            className="inline-flex items-center gap-2 text-sm text-brand-mocha hover:text-brand-blue transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            {t.menuPage.back}
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="text-xs uppercase tracking-[0.32em] text-brand-blue font-medium">
                {t.menuPage.eyebrow}
              </span>
              <h1 className="mt-4 font-serif text-5xl sm:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight text-brand-ink">
                {t.menuPage.title}
              </h1>
              <p className="mt-5 text-base text-brand-mocha max-w-xl leading-relaxed">
                {t.menuPage.subtitle}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-4 text-xs uppercase tracking-widest text-brand-mocha">
              <span className="inline-flex items-center gap-1.5">
                <ThumbsUp className="w-3.5 h-3.5 text-brand-gold" strokeWidth={2} />
                {t.menuPage.signature_pick}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-brand-coffee" strokeWidth={2} />
                {t.menuPage.hot}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Snowflake className="w-3.5 h-3.5 text-brand-blue" strokeWidth={2} />
                {t.menuPage.ice}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky filter bar */}
      <div className="sticky top-20 z-30 bg-brand-cream/85 backdrop-blur-xl border-y border-brand-border">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 py-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative w-full lg:max-w-xs">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mocha"
                strokeWidth={1.8}
              />
              <Input
                placeholder={t.menuPage.search_placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="menu-search"
                className="pl-11 h-11 rounded-full bg-white border-brand-border focus-visible:ring-brand-blue/40"
              />
            </div>

            <div
              className="flex-1 overflow-x-auto"
              data-testid="menu-filter-bar"
            >
              <div className="inline-flex gap-2 min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveCat(cat.id)}
                    data-testid={`filter-${cat.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeCat === cat.id
                        ? "bg-brand-blue text-white"
                        : "bg-white text-brand-mocha hover:text-brand-ink border border-brand-border"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menu grid */}
      <section className="mt-10 md:mt-14">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          {filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="menu-empty">
              <p className="font-serif text-2xl text-brand-ink">
                {t.menuPage.empty_title}
              </p>
              <p className="mt-2 text-sm text-brand-mocha">
                {t.menuPage.empty_subtitle}
              </p>
            </div>
          ) : (
            filtered.map((group, gi) => (
              <motion.div
                key={group.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: gi * 0.05 }}
                className="mb-16"
                data-testid={`menu-group-${group.key}`}
              >
                <div className="flex items-end justify-between mb-7">
                  <h2 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight">
                    {t.menuPage.categories[group.key]}
                  </h2>
                  <span className="text-xs uppercase tracking-widest text-brand-mocha">
                    {group.items.length}{" "}
                    {group.items.length === 1 ? "item" : "items"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {group.items.map((item, i) => (
                    <MenuItemCard
                      key={item.name}
                      item={item}
                      categoryKey={group.key}
                      index={i}
                    />
                  ))}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
